import React from "react";
import { Link } from "react-router-dom";

import dubaiVansImg from "../../assets/images/ChillerVan/chiller-fleet-chiller-van-rental-dubai-vans.webp";

export const vanRentalDubaiSections = [
  {
    type: "vr-hero",
    title: "Refrigerated Van Rental in Dubai & Fujairah — Temperature-Controlled Vans for Chilled & Frozen Cargo | Hire Today",
    paragraphs: [
      "Chiller Fleet Transport provides refrigerated van rental in Dubai and Fujairah for businesses moving chilled and frozen cargo in loads up to 1.5 tons. Our refrigerated van fleet is made up of two vehicle types: chiller vans for chilled cargo and freezer vans for frozen cargo. Every van is available self-drive or with a driver, on daily, weekly or monthly terms, and is dispatched from our Ras Al Khor headquarters and Sharjah branch. Send us your cargo type, route and dates, and our dispatch team assigns the van that fits the job.",
    ],
  },

  {
    type: "vr-table",
    title: "Choose the Right Refrigerated Van for Your Cargo",
    description: "Match the van to the condition your cargo has to travel in. Each van type has its own page with full specifications, models and rates.",
    columns: ["Van Type", "Cargo Condition", "Payload", "Suited To", "Full Details"],
    rows: [
      [
        "Chiller Van",
        "Chilled, 0°C to 8°C",
        "1 to 1.5 tons",
        "Dairy, fresh produce, flowers, catering orders",
        <Link to="/chiller-van-for-rent" className="internal-link">chiller van rental in Dubai</Link>,
      ],
      [
        "Freezer Van",
        "Frozen, 0°C to −18°C",
        "1 to 1.5 tons",
        "Frozen meat, seafood, ice cream, packaged frozen food",
        <Link to="/freezer-van-for-rent" className="internal-link">freezer van rental in Dubai</Link>,
      ],
      [
        "High Roof Dry Van",
        "Dry, general cargo",
        "1.4 to 1.8 tons",
        "Cartons, packaged goods, e-commerce parcels, exhibition materials",
        <Link to="/high-roof-dry-van-for-rent" className="internal-link">high roof van rental</Link>,
      ],
    ],
    notes: [
      <>Some jobs are too big for a van. Loads above 1.5 tons, pallet deliveries that need a tail lift, and trips that combine chilled and frozen cargo move up to our <Link to="/chiller-truck-for-rent" className="internal-link">chiller trucks</Link> or <Link to="/refrigerated-trucks-for-rent" className="internal-link">multi-temperature refrigerated trucks</Link>. Our dispatch team flags this at the quote stage, so the first booking is made on the vehicle the load needs.</>,
    ],
  },

  {
    type: "vr-steps",
    title: "How Refrigerated Van Rental Works",
    steps: [
      {
        icon: "bi-telephone",
        title: "Booking",
        description: "Send your cargo type, required temperature, pickup and drop-off points, and rental dates by phone or WhatsApp. Our Ras Al Khor dispatch team confirms the assigned van and your rate before dispatch.",
      },
      {
        icon: "bi-snow",
        title: "Pre-Cooling",
        description: "Each van is cooled to its set temperature before it leaves the branch. Loading does not begin until that temperature is confirmed.",
      },
      {
        icon: "bi-key",
        title: "Self-Drive Handover",
        description: "Self-drive vans are handed over with the refrigeration unit running, and the controls are walked through with your driver. Documents required for self-drive are confirmed with you at booking.",
      },
      {
        icon: "bi-person-badge",
        title: "With-Driver Rental",
        description: "Our driver runs your route while the driver and our dispatch team both watch cargo temperature throughout transit.",
      },
      {
        icon: "bi-geo-alt",
        title: "Tracking",
        description: "Every van carries GPS and onboard temperature logging, giving each trip a full temperature record from departure to final drop.",
      },
    ],
  },

  {
    type: "vr-text-image",
    title: "Refrigerated Van Rental Across Dubai & Sharjah",
    image: dubaiVansImg,
    imageAlt: "Chiller Fleet refrigerated vans ready for rental in Dubai and Sharjah",
    paragraphs: [
      "Across Dubai, our refrigerated vans handle the delivery work suited to a compact vehicle: multi-drop restaurant and grocery restocking, catering runs, and deliveries into the tight streets of Deira, Bur Dubai and Karama. Vans dispatched from Ras Al Khor and Al Barsha cover central, northern and western Dubai, including Al Quoz, Business Bay and Dubai Marina. Drop orders are sequenced to keep door openings between stops to a minimum, which holds cargo temperature steady through long multi-drop days.",
      "In Sharjah, vans operate from our Sharjah branch across the industrial areas, Sharjah city and its commercial districts. During peak summer months and Ramadan, van routes in both Dubai and Sharjah shift to night and early-morning dispatch, so drops finish ahead of the hottest hours and daytime traffic.",
    ],
  },

  {
    type: "vr-subsections",
    title: "Refrigerated Van Rental in Fujairah",
    intro: "Chiller Fleet Transport runs refrigerated van rental in Fujairah for chilled and frozen deliveries along the east coast. Chiller van rental in Fujairah and freezer van rental in Fujairah are both booked through our Dubai dispatch team.",
    subsections: [
      {
        icon: "bi-signpost-split",
        title: "Getting Vans to the East Coast",
        paragraphs: [
          "Vans leave pre-cooled from Ras Al Khor or our Sharjah branch and cross the Hajar Mountains with the refrigeration unit running the whole way. GPS and temperature logging stay live for the full inter-emirate run, so the reading at delivery can be checked against the reading at departure. On summer days, early dispatch gets the mountain crossing and first drops done before peak heat. Single-day runs, weekly routes and monthly east coast contracts are all available.",
        ],
      },
      {
        icon: "bi-geo-alt-fill",
        title: "Fujairah Coverage",
        paragraphs: [
          <>Our vans deliver across Fujairah City, the Fujairah Free Zone, the port and industrial areas, Dibba Al Fujairah and Masafi. Khor Fakkan and Kalba, Sharjah's east coast towns, are served on the same routes. Heavier east coast loads are handled through our <Link to="/truck-rental-in-fujairah" className="internal-link">temperature-controlled truck rental in Fujairah</Link>.</>,
        ],
      },
    ],
  },

  {
    type: "vr-table",
    title: "Refrigerated Van Rental Rates",
    description: "The rates below are starting daily rates. Weekly and monthly contracts carry a lower daily equivalent than single-day rental, and full weekly and monthly rates are listed on each van page. Final pricing is confirmed based on cargo type, route and rental duration.",
    columns: ["Van Type", "Daily, Self-Drive", "Daily, With Driver"],
    rows: [
      ["Chiller Van", "From AED 250", "From AED 350"],
      ["Freezer Van", "From AED 450", "From AED 500"],
    ],
    compact: true,
    notes: [
      <><strong>Fujairah inter-emirate surcharge:</strong> AED 50 to AED 200, depending on van type.</>,
    ],
  },

  {
    type: "vr-fleet",
    title: "Refrigerated Van Fleet at a Glance",
    description: "136 refrigerated vans, made up of 78 chiller vans and 58 freezer vans, plus 28 high roof dry vans for general cargo. The fleet is fully RTA compliant, driven by a team of 200+ drivers averaging over five years' experience, and has been operating across the UAE since 2013.",
    stats: [
      { value: "136", label: "Refrigerated Vans" },
      { value: "78", label: "Chiller Vans" },
      { value: "58", label: "Freezer Vans" },
      { value: "28", label: "High Roof Dry Vans" },
      { value: "200+", label: "Experienced Drivers" },
      { value: "2013", label: "Operating Since" },
    ],
  },

  {
    type: "vr-faq",
    title: "Frequently Asked Questions",
    faqs: [
      {
        q: "How quickly can a refrigerated van be dispatched?",
        a: "Standby vans are held at our Ras Al Khor and Sharjah branches for same-day urgent bookings. For planned routes and contract work, booking a day ahead lets us confirm the exact van and dispatch slot.",
      },
      {
        q: "Can I add a driver to a self-drive rental?",
        a: "Yes. A driver can be added at any point during a self-drive rental, charged at the with-driver rate listed for that van on its rate page. The driver takes over the route and temperature checks from the day they join.",
      },
      {
        q: "What happens if a van develops a fault during my rental?",
        a: "In-house mechanics at each branch handle repairs directly, with no third-party workshop. Where a van cannot be fixed on the spot, a standby van is sent to keep your deliveries running.",
      },
      {
        q: "Can I book several vans for the same job?",
        a: "Yes. Multi-van bookings are dispatched from both Ras Al Khor and Sharjah, which suits peak trading periods, event catering and routes that split between Dubai and the east coast.",
      },
      {
        q: "Do the vans have side door access for multi-drop deliveries?",
        a: "Yes. Chiller and freezer vans have both rear and side door access, so drivers can unload individual orders at each stop without opening the full cargo area.",
      },
    ],
  },

  {
    type: "cta",
    title: "Book a Refrigerated Van in Dubai or Fujairah",
    paragraphs: [
      <>Call or WhatsApp our Ras Al Khor dispatch team with your cargo type, route and dates. We confirm the van, the rate and the dispatch time, whether the job runs across Dubai, Sharjah or the east coast. <Link to="/contact-us" className="text-light fw-bold">Contact Chiller Fleet Transport</Link> to book today.</>,
    ],
  },
];
